import type { MouseEvent, ReactNode, Ref } from 'react';
import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { BreadcrumbLinkClickPayload } from '@noths/polaris-design-system-candidate-components';
import { Breadcrumbs } from '@noths/polaris-design-system-candidate-components';

import { OfferLabel } from 'src/components/atoms/OfferLabel/OfferLabel';
import { ProductBadge } from 'src/components/atoms/ProductBadge/ProductBadge';
import { MadeToOrder } from 'src/components/molecules/MadeToOrder/MadeToOrder';
import { PartnerMiniModule } from 'src/components/molecules/PartnerMiniModule/PartnerMiniModule';
import { ProductFeaturesWithTracking } from 'src/components/molecules/ProductFeatures/components/ProductFeaturesWithTracking';
import { ProductPrice } from 'src/components/molecules/ProductPrice/ProductPrice';
import { ProductRatingWithTracking } from 'src/components/molecules/ProductRating/ProductRatingWithTracking';
import type { PDPViewModel } from './types';
import * as styles from './PDPLayoutView.styles';

export interface PDPLayoutViewProps {
  /** Ref to the CTA grid cell, for drawers rendered outside the view. Only attaches when ctaTrigger is provided. */
  ctaColumnRef?: Ref<HTMLDivElement>;
  /** CTA trigger area (delivery summary, options trigger, basket CTA). Not an inline options section. */
  ctaTrigger?: ReactNode;
  /** Wider column, spans rows alongside the narrow-column content on desktop */
  images?: ReactNode;
  linkCarousel?: ReactNode;
  membershipBanner?: ReactNode;
  /** Called when a breadcrumb link is clicked; not called when viewModel.breadcrumbs is empty */
  onBreadcrumbLinkClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    payload: BreadcrumbLinkClickPayload,
  ) => void;
  /** Called when the PartnerMiniModule first scrolls into the viewport */
  onPartnerMiniModuleVisible?: () => void;
  /** Full-width section with tinted background */
  partnerModules?: ReactNode;
  productInformation?: ReactNode;
  publicAttributes?: ReactNode;
  recommendedProducts?: ReactNode;
  /** Slot content owns the reviews fragment id and its internal layout */
  reviews?: ReactNode;
  /** Rendered last; caller provides sticky wrapper and trigger wiring */
  stickyCta?: ReactNode;
  trustCarousel?: ReactNode;
  viewModel: PDPViewModel;
}

export const PDPLayoutView = ({
  ctaColumnRef,
  ctaTrigger,
  images,
  linkCarousel,
  membershipBanner,
  onBreadcrumbLinkClick,
  onPartnerMiniModuleVisible,
  partnerModules,
  productInformation,
  publicAttributes,
  recommendedProducts,
  reviews,
  stickyCta,
  trustCarousel,
  viewModel,
}: PDPLayoutViewProps) => {
  const showRating = Boolean(viewModel.productReviews.rating);

  return (
    <>
      <div css={visuallyHidden} role="status">
        {viewModel.pageLiveRegionText}
      </div>
      <div css={[styles.maxWidthContainer, styles.grid]}>
        {viewModel.breadcrumbs.length > 0 && (
          <div css={styles.breadcrumbsArea}>
            <Breadcrumbs links={viewModel.breadcrumbs} onLinkClick={onBreadcrumbLinkClick} />
          </div>
        )}
        <div css={styles.imagesArea}>{images}</div>
        <div css={styles.titleArea}>
          <h1 css={[styles.productTitle]}>{viewModel.title}</h1>
          <div
            css={styles.descriptionArea}
            dangerouslySetInnerHTML={{ __html: viewModel.description }}
          />
          <section css={styles.priceRow}>
            <ProductPrice
              preSalePrices={viewModel.price.preSalePrices}
              price={viewModel.price.price}
              priceIsMinimum={viewModel.price.priceIsMinimum}
              salePercentage={viewModel.price.salePercentage}
              salePercentageInRed={viewModel.price.salePercentageInRed}
            />
            {(viewModel.badge || showRating) && (
              <div css={styles.ratingPill}>
                <ProductRatingWithTracking
                  averageRating={viewModel.productReviews.rating ?? undefined}
                  numberOfReviews={viewModel.productReviews.count}
                  reviewsLinkHref={viewModel.productReviews.href}
                />
                {viewModel.badge && <ProductBadge badge={viewModel.badge} />}
              </div>
            )}
          </section>
          {viewModel.offerLabel && (
            <div css={styles.offerLabel}>
              <OfferLabel {...viewModel.offerLabel} />
            </div>
          )}
          {viewModel.showMadeToOrder && <MadeToOrder />}
          {ctaTrigger && (
            <div css={styles.ctaTriggerStack} ref={ctaColumnRef}>
              {ctaTrigger}
            </div>
          )}
          <PartnerMiniModule
            {...viewModel.partner}
            onVisible={onPartnerMiniModuleVisible}
            partnerRating={viewModel.partnerReviews.rating}
            partnerReviewCount={viewModel.partnerReviews.count}
          />
          {viewModel.productFeatures.length > 0 && (
            <ProductFeaturesWithTracking
              currency={viewModel.price.price.currency}
              minimumOptionsCost={viewModel.price.minimumOptionsCost}
              productFeatures={viewModel.productFeatures}
            />
          )}
        </div>
      </div>
      <div css={[styles.maxWidthContainer, styles.column]}>
        {reviews}
        <div css={styles.productDetailsWrapper}>
          {productInformation && (
            <div css={styles.productInformationArea}>{productInformation}</div>
          )}
          {publicAttributes && <div css={styles.publicAttributesArea}>{publicAttributes}</div>}
        </div>
        {partnerModules}
        {recommendedProducts}
        {trustCarousel}
        {membershipBanner}
        {linkCarousel}
        {stickyCta}
      </div>
    </>
  );
};
